const CONTEXT_PX = 64; // roughly 1-2 message rows of read context above the first unread one
const BUTTON_TOLERANCE_PX = 8; // just enough to absorb subpixel rounding
const AUTOSCROLL_TOLERANCE_PX = 24; // "a line or so"
const RESIZE_PIN_TOLERANCE_PX = 48; // "less than 2 lines"
const PULSE_DURATION_MS = 600;

const distanceFromBottom = (el: HTMLElement) => el.scrollHeight - el.scrollTop - el.clientHeight;

const pluralize = (count: number, singular: string, plural: string) => (count === 1 ? singular : plural);

const setupConversationScroll = (container: HTMLElement) => {
  if (container.dataset.scrollWired) return;
  container.dataset.scrollWired = "true";

  const button = document.getElementById("conversation_jump_button");
  const label = document.getElementById("conversation_jump_button_label");
  if (!button || !label) return;

  const belowFold = new Set<string>();

  const updateButton = () => {
    const atBottom = distanceFromBottom(container) <= BUTTON_TOLERANCE_PX;
    button.classList.toggle("hidden", atBottom);
    label.textContent =
      belowFold.size > 0
        ? `${belowFold.size} ${pluralize(belowFold.size, "neue Nachricht", "neue Nachrichten")}`
        : "nach unten springen";
  };

  const visibilityObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          belowFold.delete(entry.target.id);
        } else {
          belowFold.add(entry.target.id);
        }
      });
      updateButton();
    },
    { root: container, threshold: 0 },
  );

  const observeIfUnread = (el: Element) => {
    if (!(el instanceof HTMLElement) || el.dataset.messageUnread === undefined) return;
    visibilityObserver.observe(el);
  };

  container.querySelectorAll("[data-message-unread]").forEach(observeIfUnread);

  const divider = document.getElementById("first_unread_divider");
  if (divider) {
    container.scrollTop = Math.max(0, divider.offsetTop - CONTEXT_PX);
  } else {
    container.scrollTop = container.scrollHeight;
  }
  updateButton();

  // Tracks how far from the bottom the container was *before* whatever just changed it
  // (a new message appended, the composer resizing, or the user scrolling) — every
  // "should I snap to the bottom" decision below needs this pre-change value, since by
  // the time an append/resize is observed, scrollHeight has already grown to include it
  // and a fresh distanceFromBottom() call would almost always read as "far from bottom."
  let lastKnownDistance = distanceFromBottom(container);

  let ticking = false;
  container.addEventListener("scroll", () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      updateButton();
      lastKnownDistance = distanceFromBottom(container);
      ticking = false;
    });
  });

  button.addEventListener("click", () => {
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    lastKnownDistance = 0;
  });

  // Live arrival: Turbo Stream appends land here regardless of the specific delivery
  // mechanism (Turbo::Train/Mercure in this app), so a childList MutationObserver is the
  // one hook guaranteed to fire for every new message.
  const arrivalObserver = new MutationObserver((mutations) => {
    let appended = false;
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        appended = true;
        observeIfUnread(node);
      });
    });
    if (!appended) return;

    if (lastKnownDistance <= AUTOSCROLL_TOLERANCE_PX) {
      container.scrollTop = container.scrollHeight;
    } else if (!button.classList.contains("hidden")) {
      button.classList.add("animate-jump-pulse");
      setTimeout(() => button.classList.remove("animate-jump-pulse"), PULSE_DURATION_MS);
    }
    updateButton();
    lastKnownDistance = distanceFromBottom(container);
  });
  arrivalObserver.observe(container, { childList: true });

  // Compose-resize pinning: the composer growing/shrinking below this flex sibling
  // changes this container's own clientHeight, so observing it directly (rather than the
  // composer) covers every cause (image attached, textarea grows, etc.) in one place.
  const resizeObserver = new ResizeObserver(() => {
    if (lastKnownDistance <= RESIZE_PIN_TOLERANCE_PX) container.scrollTop = container.scrollHeight;
    lastKnownDistance = distanceFromBottom(container);
  });
  resizeObserver.observe(container);
};

const enableConversationScroll = () => {
  document.querySelectorAll<HTMLElement>(".conversation-messages-scroll").forEach(setupConversationScroll);
};

document.addEventListener("turbo:load", enableConversationScroll);
document.addEventListener("turbo:morph", enableConversationScroll);
// Switching conversations in the sidebar navigates within <turbo-frame id="conversation_detail">,
// which only fires turbo:frame-load, not turbo:load (same gap fixed in uppy.ts).
document.addEventListener("turbo:frame-load", enableConversationScroll);
